import { tv } from 'tailwind-variants'

import { typography } from '@gto/ui/typography'

/**
 * LinearProgress styles — GTOW Design System `<Progress> | Linear`
 * (file `s5knlq42XjwZx88JuPw8c7`, node `6586:46855`).
 *
 * `track` is the 4px-tall hairline rail; `bar` is the moving fill.
 */
export const linearProgressVariants = tv({
  slots: {
    root: 'flex w-full items-center gap-2',
    track: 'relative w-full overflow-hidden rounded-md',
    bar: 'absolute inset-y-0 left-0',
    label: ['shrink-0 whitespace-nowrap text-text-primary', typography.body3],
  },
  variants: {
    color: {
      primary: {
        track: 'bg-electric-green-A800',
        bar: 'bg-primary-main',
      },
      'primary-contrast': {
        track: 'bg-black',
        bar: 'bg-primary-main',
      },
      secondary: {
        track: 'bg-secondary-main',
        bar: 'bg-secondary-main',
      },
      inherit: {
        track: 'bg-secondary-main',
        bar: 'bg-text-primary',
      },
      info: {
        track: 'bg-secondary-main',
        bar: 'bg-info-main',
      },
      error: {
        track: 'bg-secondary-main',
        bar: 'bg-error-main',
      },
      warning: {
        track: 'bg-secondary-main',
        bar: 'bg-warning-main',
      },
    },
    // Track height. `sm` (4px) is the design-system hairline default; `md` (8px)
    // and `lg` (12px) match the heavier bars some product surfaces use.
    size: {
      sm: { track: 'h-1' },
      md: { track: 'h-2' },
      lg: { track: 'h-3' },
    },
    type: {
      determinate: {
        bar: 'transition-[width] duration-700 ease-out',
      },
      indeterminate: {
        // Track-width bar with a LEFT origin: the keyframes drive
        // `translateX(L%) scaleX(S)` (compositor-thread, so the loop keeps moving while a
        // re-render saturates the main thread), and that math is only MUI-equivalent when
        // the percentage basis is the full track and the scale stretches from the left edge.
        // No `will-change` — a running transform animation is already compositor-promoted.
        bar: 'w-full origin-left',
      },
    },
  },
  defaultVariants: {
    color: 'primary',
    size: 'sm',
    type: 'determinate',
  },
})

export type LinearProgressVariants = Parameters<typeof linearProgressVariants>[0]
